/* eslint-disable react-hooks/refs -- @dnd-kit node refs are used during render by design */
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type Announcements,
  type CollisionDetection,
  type DragEndEvent,
  type ScreenReaderInstructions,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDownIcon, ChevronUpIcon, GripVerticalIcon } from 'lucide-react';
import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import type {
  DashboardWidgetId,
  DashboardZoneId,
} from '@/products/helpdesk/features/dashboard/dashboard-layout';
import { cn } from '@/lib/utils';

export type DashboardZoneDragData = { type: 'zone'; zoneId: DashboardZoneId };
export type DashboardWidgetDragData = {
  type: 'widget';
  zoneId: DashboardZoneId;
  widgetId: DashboardWidgetId;
};
export type DashboardDragData = DashboardZoneDragData | DashboardWidgetDragData;

export function dashboardZoneSortableId(zoneId: DashboardZoneId): string {
  return `dashboard-zone:${zoneId}`;
}

export function dashboardWidgetSortableId(
  zoneId: DashboardZoneId,
  widgetId: DashboardWidgetId,
): string {
  return `dashboard-widget:${zoneId}:${widgetId}`;
}

export function asDashboardDragData(value: unknown): DashboardDragData | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const data = value as Partial<DashboardDragData>;
  if (data.type === 'zone' && typeof data.zoneId === 'string') {
    return data as DashboardZoneDragData;
  }
  if (
    data.type === 'widget' &&
    typeof data.zoneId === 'string' &&
    typeof data.widgetId === 'string'
  ) {
    return data as DashboardWidgetDragData;
  }
  return null;
}

export function useDashboardDndSensors() {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
}

export const dashboardCollisionDetection: CollisionDetection = (args) => {
  const activeData = asDashboardDragData(args.active.data.current);
  if (activeData?.type === 'zone') {
    return closestCenter({
      ...args,
      droppableContainers: args.droppableContainers.filter((container) => {
        const data = asDashboardDragData(container.data.current);
        return data?.type === 'zone';
      }),
    });
  }
  if (activeData?.type === 'widget') {
    return closestCenter({
      ...args,
      droppableContainers: args.droppableContainers.filter((container) => {
        const data = asDashboardDragData(container.data.current);
        return data?.type === 'widget' && data.zoneId === activeData.zoneId;
      }),
    });
  }
  return closestCenter(args);
};

const DASHBOARD_DND_SCREEN_READER: ScreenReaderInstructions = {
  draggable: `
Zum Aufnehmen: Leertaste oder Enter.
Mit den Pfeiltasten verschieben.
Ablegen: Leertaste oder Enter.
Abbrechen: Escape.
`,
};

function dashboardDndAnnouncements(): Announcements {
  return {
    onDragStart({ active }) {
      const data = asDashboardDragData(active.data.current);
      if (data?.type === 'zone') {
        return `Zone aufgenommen.`;
      }
      if (data?.type === 'widget') {
        return `Widget aufgenommen.`;
      }
      return `Element aufgenommen.`;
    },
    onDragOver({ active, over }) {
      if (!over) {
        return;
      }
      const activeData = asDashboardDragData(active.data.current);
      const overData = asDashboardDragData(over.data.current);
      if (activeData?.type === 'zone' && overData?.type === 'zone') {
        return `Zone über anderer Zone.`;
      }
      if (activeData?.type === 'widget' && overData?.type === 'widget') {
        return `Widget über anderem Widget.`;
      }
      return;
    },
    onDragEnd({ active, over }) {
      const activeData = asDashboardDragData(active.data.current);
      if (!over) {
        return activeData?.type === 'zone'
          ? `Zonen-Verschieben abgebrochen.`
          : `Widget-Verschieben abgebrochen.`;
      }
      if (activeData?.type === 'zone') {
        return `Zone abgelegt.`;
      }
      if (activeData?.type === 'widget') {
        return `Widget abgelegt.`;
      }
      return `Element abgelegt.`;
    },
    onDragCancel({ active }) {
      const data = asDashboardDragData(active.data.current);
      if (data?.type === 'zone') {
        return `Zonen-Verschieben abgebrochen.`;
      }
      if (data?.type === 'widget') {
        return `Widget-Verschieben abgebrochen.`;
      }
      return `Verschieben abgebrochen.`;
    },
  };
}

export function DashboardDndProvider({
  enabled,
  onDragEnd,
  children,
}: {
  enabled: boolean;
  onDragEnd: (event: DragEndEvent) => void;
  children: ReactNode;
}) {
  const sensors = useDashboardDndSensors();
  if (!enabled) {
    return children;
  }
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={dashboardCollisionDetection}
      onDragEnd={onDragEnd}
      accessibility={{
        announcements: dashboardDndAnnouncements(),
        screenReaderInstructions: DASHBOARD_DND_SCREEN_READER,
      }}
    >
      {children}
    </DndContext>
  );
}

export function DashboardZoneSortableContext({
  zoneIds,
  children,
}: {
  zoneIds: readonly DashboardZoneId[];
  children: ReactNode;
}) {
  return (
    <SortableContext
      items={zoneIds.map(dashboardZoneSortableId)}
      strategy={verticalListSortingStrategy}
    >
      {children}
    </SortableContext>
  );
}

export function DashboardWidgetSortableContext({
  zoneId,
  widgetIds,
  children,
}: {
  zoneId: DashboardZoneId;
  widgetIds: readonly DashboardWidgetId[];
  children: ReactNode;
}) {
  return (
    <SortableContext
      items={widgetIds.map((id) => dashboardWidgetSortableId(zoneId, id))}
      strategy={rectSortingStrategy}
    >
      {children}
    </SortableContext>
  );
}

export function DashboardDragHandle({
  label,
  roleDescription,
  attributes,
  listeners,
}: {
  label: string;
  /** Spoken role, e.g. „sortierbare Zone“ / „sortierbares Widget“ */
  roleDescription: string;
  attributes: HTMLAttributes<HTMLButtonElement>;
  listeners?: Record<string, unknown>;
}) {
  return (
    <button
      type="button"
      className="inline-flex size-9 shrink-0 cursor-grab touch-none items-center justify-center rounded-md border bg-background text-muted-foreground hover:text-foreground active:cursor-grabbing"
      aria-label={label}
      aria-roledescription={roleDescription}
      {...attributes}
      {...listeners}
    >
      <GripVerticalIcon className="size-4" aria-hidden />
    </button>
  );
}

export function DashboardMoveButtons({
  label,
  canUp,
  canDown,
  onUp,
  onDown,
}: {
  label: string;
  canUp: boolean;
  canDown: boolean;
  onUp: () => void;
  onDown: () => void;
}) {
  return (
    <div className="flex shrink-0 items-center gap-0.5">
      <Button
        type="button"
        variant="outline"
        size="icon-sm"
        disabled={!canUp}
        aria-label={`${label} nach oben`}
        onClick={onUp}
      >
        <ChevronUpIcon className="size-4" aria-hidden />
      </Button>
      <Button
        type="button"
        variant="outline"
        size="icon-sm"
        disabled={!canDown}
        aria-label={`${label} nach unten`}
        onClick={onDown}
      >
        <ChevronDownIcon className="size-4" aria-hidden />
      </Button>
    </div>
  );
}

export function SortableDashboardZone({
  zoneId,
  title,
  disabled,
  children,
}: {
  zoneId: DashboardZoneId;
  title: string;
  disabled?: boolean;
  children: (args: {
    setNodeRef: (node: HTMLElement | null) => void;
    style: CSSProperties | undefined;
    isDragging: boolean;
    dragHandle: ReactNode;
  }) => ReactNode;
}) {
  const sortable = useSortable({
    id: dashboardZoneSortableId(zoneId),
    data: { type: 'zone', zoneId } satisfies DashboardZoneDragData,
    disabled,
  });

  const style = disabled
    ? undefined
    : {
        transform: CSS.Translate.toString(sortable.transform),
        transition: sortable.transition,
      };

  return children({
    setNodeRef: sortable.setNodeRef,
    style,
    isDragging: sortable.isDragging,
    dragHandle: (
      <DashboardDragHandle
        label={`Zone ${title} verschieben`}
        roleDescription="sortierbare Zone"
        attributes={sortable.attributes}
        listeners={sortable.listeners}
      />
    ),
  });
}

export function SortableDashboardWidget({
  zoneId,
  widgetId,
  title,
  disabled,
  className,
  children,
}: {
  zoneId: DashboardZoneId;
  widgetId: DashboardWidgetId;
  title: string;
  disabled?: boolean;
  className?: string;
  children: (args: { dragHandle: ReactNode; isDragging: boolean }) => ReactNode;
}) {
  const sortable = useSortable({
    id: dashboardWidgetSortableId(zoneId, widgetId),
    data: { type: 'widget', zoneId, widgetId } satisfies DashboardWidgetDragData,
    disabled,
  });

  const style = disabled
    ? undefined
    : {
        transform: CSS.Translate.toString(sortable.transform),
        transition: sortable.transition,
      };

  return (
    <div
      ref={sortable.setNodeRef}
      style={style}
      className={cn(sortable.isDragging && 'opacity-60', className)}
      data-dashboard-sortable-widget={widgetId}
    >
      {children({
        isDragging: sortable.isDragging,
        dragHandle: (
          <DashboardDragHandle
            label={`Widget ${title} verschieben`}
            roleDescription="sortierbares Widget"
            attributes={sortable.attributes}
            listeners={sortable.listeners}
          />
        ),
      })}
    </div>
  );
}

export type { UniqueIdentifier };
